import {
  employeeSizeList,
  jobTitleList,
  pronounList,
} from "#src/common/helper/contactSupport/contactDetailFields";
import { createToast } from "#src/common/system/toasts";
import { ButtonV2 } from "#src/commonV2/Button";
import { FormDropdown } from "#src/commonV2/form/Dropdown/FormDropdown";
import { useFormDropdown } from "#src/commonV2/form/Dropdown/useFormDropdown";
import { FormInput } from "#src/commonV2/form/Input/FormInput";
import { useFormInput } from "#src/commonV2/form/Input/useFormInput";
import { FormInputLeadingDropdown } from "#src/commonV2/form/InputLeadingDropdown/FormInputLeadingDropdown";
import { useFormInputLeadingDropdown } from "#src/commonV2/form/InputLeadingDropdown/useFormInputLeadingDropdown";
import { ModalV2 } from "#src/commonV2/modal/ModalV2";
import { ModalHeaderIcon } from "#src/commonV2/modal/ModalHeaderIcon";
import { IFilterItemV2 } from "#src/commonV2/Table/Filter/types";
import constants from "#src/config/constants";
import patterns from "#src/config/patterns";
import { endpointService } from "#src/services/endpoint";
import workspaceServices from "#src/services/workspace";
import { useAppSelector } from "#src/store/hooks";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";
import { CheckCircle } from "@untitled-ui/icons-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

interface IEndpointInstallAgentModalProps {
  workspaceId: string;
  open: boolean;
  onClose: () => void;
}

export const EndpointInstallAgentModal = ({
  workspaceId,
  open,
  onClose,
}: IEndpointInstallAgentModalProps) => {
  const { t } = useTranslation("endpoint", {
    keyPrefix: "devices.modals.installAgent",
  });

  const userInfo = useAppSelector((state) => state.auth.userInfo);

  // --------------- STATES ---------------

  const [defaultStates, setDefaultStates] = useState<{
    industry: string;
    companySize: IFilterItemV2;
  }>({
    industry: "",
    companySize: employeeSizeList[0],
  });

  const {
    value: fullName,
    selected: pronoun,
    reset: resetFullNameField,
    renderProps: fullNameFieldRenderProps,
  } = useFormInputLeadingDropdown({
    id: "fullName",
    label: t("fields.fullName.label"),
    placeholder: t("fields.fullName.placeholder"),
    defaultValue: userInfo?.full_name,
    options: pronounList,
    defaultSelected: pronounList[0],
    validation: { required: true },
  });

  const {
    selected: jobTitle,
    reset: resetJobTitleField,
    renderProps: jobTitleFieldRenderProps,
  } = useFormDropdown({
    id: "jobTitle",
    label: t("fields.jobTitle.label"),
    defaultSelected: jobTitleList[0],
    options: jobTitleList,
    requiredMark: true,
  });

  const {
    value: email,
    reset: resetEmailField,
    renderProps: emailFieldRenderProps,
  } = useFormInput({
    label: t("fields.email.label"),
    placeholder: t("fields.email.placeholder"),
    defaultValue: userInfo?.email,
    validation: { required: true, pattern: patterns.EMAIL },
  });

  const {
    value: phoneNumber,
    reset: resetPhoneNumberField,
    renderProps: phoneNumberFieldRenderProps,
  } = useFormInput({
    label: t("fields.phoneNumber.label"),
    placeholder: t("fields.phoneNumber.placeholder"),
    defaultValue: userInfo?.phone,
    validation: { required: true, pattern: patterns.PHONE },
  });

  const {
    value: industry,
    reset: resetIndustryField,
    renderProps: industryFieldRenderProps,
  } = useFormInput({
    label: t("fields.industry.label"),
    placeholder: t("fields.industry.placeholder"),
    defaultValue: defaultStates.industry,
    validation: { required: true },
  });

  const {
    selected: companySize,
    reset: resetCompanySizeField,
    renderProps: companySizeFieldRenderProps,
  } = useFormDropdown({
    id: "companySize",
    label: t("fields.companySize.label"),
    defaultSelected: defaultStates.companySize,
    options: employeeSizeList,
    requiredMark: true,
  });

  const [openSuccessModal, setOpenSuccessModal] = useState<boolean>(false);

  // --------------- METHODS ---------------

  const onCloseAndCleanup = () => {
    onClose();
    resetFullNameField();
    resetJobTitleField();
    resetEmailField();
    resetPhoneNumberField();
    resetIndustryField();
    resetCompanySizeField();
  };

  const onSubmitRequest = () => {
    if (fullName.length === 0) {
      createToast({ type: "warning", message: t("fields.fullName.required") });
      return;
    }
    if (email.length === 0) {
      createToast({ type: "warning", message: t("fields.email.required") });
      return;
    }
    if (!patterns.EMAIL.test(email)) {
      createToast({ type: "warning", message: t("fields.email.invalid") });
      return;
    }
    if (phoneNumber.length === 0) {
      createToast({
        type: "warning",
        message: t("fields.phoneNumber.required"),
      });
      return;
    }
    if (!patterns.PHONE.test(phoneNumber)) {
      createToast({
        type: "warning",
        message: t("fields.phoneNumber.invalid"),
      });
      return;
    }
    if (industry.length === 0) {
      createToast({ type: "warning", message: t("fields.industry.required") });
      return;
    }
    endpointService
      .createInstallAgent(workspaceId, {
        gender: pronoun.value ?? "",
        fullname: fullName,
        phone_number: phoneNumber,
        job_title: jobTitle.value ?? "",
        industry: industry,
        employee_size: companySize.value ?? "",
        email: email,
      })
      .then(() => {
        onCloseAndCleanup();
        setOpenSuccessModal(true);
      })
      .catch(apiErrorHandler);
  };

  // --------------- EFFECTS ---------------

  useEffect(() => {
    workspaceServices
      .retrieve_workspace(workspaceId)
      .then((response) => {
        const getEmployeeSizeItem = employeeSizeList.find(
          (item) => item.value === response.employee_size
        );
        setDefaultStates((prev) => ({
          ...prev,
          industry: response.industry,
          companySize: getEmployeeSizeItem
            ? getEmployeeSizeItem
            : prev.companySize,
        }));
      })
      .catch(apiErrorHandler);
  }, [workspaceId]);

  // --------------- RENDER ---------------

  return (
    <>
      <ModalV2
        open={open}
        onChangeOpen={onCloseAndCleanup}
        className="w-[50rem]"
      >
        <div className="flex flex-col gap-5">
          <span className="text-lg font-semibold text-gray-v2-900">
            {t("title")}
          </span>
          <span className="text-md text-gray-v2-600 whitespace-pre-line">
            {t("hint")}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-6">
          <FormInputLeadingDropdown {...fullNameFieldRenderProps} />
          <FormDropdown {...jobTitleFieldRenderProps} />
          <FormInput {...emailFieldRenderProps} />
          <FormInput {...phoneNumberFieldRenderProps} />
          <FormInput {...industryFieldRenderProps} />
          <FormDropdown {...companySizeFieldRenderProps} />
          <div className="col-span-2 flex justify-end gap-2">
            <ButtonV2
              variant="secondary"
              color="gray"
              onClick={onCloseAndCleanup}
            >
              {t("button.cancel")}
            </ButtonV2>
            <ButtonV2 onClick={onSubmitRequest}>
              {t("button.sendRequest")}
            </ButtonV2>
          </div>
        </div>
      </ModalV2>
      <ModalV2
        open={openSuccessModal}
        onChangeOpen={() => {
          setOpenSuccessModal(false);
        }}
        className="w-[34rem] gap-8"
      >
        <div className="flex gap-4">
          <ModalHeaderIcon type="success" Icon={CheckCircle} />
          <div className="flex flex-col gap-1">
            <span className="text-lg font-semibold text-gray-v2-900">
              {t("successModal.title")}
            </span>
            <span className="text-sm text-gray-v2-600 whitespace-pre-line">
              {t("successModal.hint")}{" "}
              <Link
                to={`mailto:${constants.CYSTACK_SUPPORT}`}
                target="_blank"
                className="font-semibold text-brand-v2-600 hover:text-brand-v2-700"
              >
                {constants.CYSTACK_SUPPORT}
              </Link>
            </span>
          </div>
        </div>
        <div className="flex justify-end">
          <ButtonV2
            variant="secondary"
            color="gray"
            onClick={() => {
              setOpenSuccessModal(false);
            }}
          >
            {t("successModal.button.ok")}
          </ButtonV2>
        </div>
      </ModalV2>
    </>
  );
};
